import { useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  useAdminDeleteMarker,
  useAdminUpdateMarker,
  useGetGatheringSpotsAll,
  useGetMarkerById,
} from '@outdoria/api';
import { Marker } from '@outdoria/types';
import { Button, Loader, QueryBoundary } from '@outdoria/ui';
import { ActionBar } from '../../../containers';
import { Form } from '../../../components';
import { useForm } from '../../../hooks';

import s from './styles.module.scss';

type MarkerProps = {
  selectedMarker: string;
  onExit: () => void;
};
export const MarkerEdit = (props: MarkerProps) => (
  <QueryBoundary fallback={<Loader />}>
    <Load {...props} />
  </QueryBoundary>
);

const Load = ({ selectedMarker, onExit }: MarkerProps) => {
  const { data: marker } = useGetMarkerById(selectedMarker);
  const { mutate: mutateUpdate, isSuccess: isSuccessUpdate } =
    useUpdate(selectedMarker);
  const { mutate: mutateDelete, isSuccess: isSuccessDelete } = useDelete();
  const { gatheringSpotOptions } = useOptions();
  const { register, form, onReset } = useForm<Marker>({ ...marker });

  const onClearState = () => {
    onReset();
    if (onExit) onExit();
  };

  const onCreate = () => {
    mutateUpdate(form);
  };

  const onDelete = () => {
    const shouldDelete = confirm('Do you really want to delete this marker?');
    if (shouldDelete) {
      mutateDelete(selectedMarker);
    }
  };

  useEffect(() => {
    if (isSuccessUpdate || isSuccessDelete) {
      onClearState();
    }
  }, [isSuccessUpdate, isSuccessDelete]);

  return (
    <>
      <ActionBar
        buttons={
          <>
            <Button
              label="Cancel"
              inverted
              simple
              variant={Button.Variant.GHOST}
              onClick={onClearState}
            />
            <Button
              label="Delete marker"
              variant={Button.Variant.DANGER}
              onClick={onDelete}
            />
            <Button
              label="Update marker"
              variant={Button.Variant.ACTION}
              onClick={onCreate}
            />
          </>
        }
      />

      <div className={s.formContainer}>
        <Form pretty>
          <Form.Input
            label="Latitude"
            {...register('coords_x', { required: true })}
          />

          <Form.Input
            label="Longtitude"
            {...register('coords_y', { required: true })}
          />

          <Form.Select
            label="Gathering spot"
            data={gatheringSpotOptions}
            {...register('gathering_spot_id', { required: true })}
          />
        </Form>
      </div>
    </>
  );
};

const useOptions = () => {
  const { data: gatheringSpots } = useGetGatheringSpotsAll();
  const gatheringSpotOptions = gatheringSpots.map(spot => ({
    value: spot.gathering_spot_id,
    label: spot.name,
  }));

  return { gatheringSpotOptions };
};

const useUpdate = (markerId: string) => {
  const { mutate, error, isSuccess } = useAdminUpdateMarker(markerId);

  useEffect(() => {
    if (error) toast.error(error?.message ?? 'Something went wrong :c');
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Yay! Marker updated :3`);
    }
  }, [isSuccess]);

  return { mutate, error, isSuccess };
};

const useDelete = () => {
  const { mutate, error, isSuccess } = useAdminDeleteMarker();

  useEffect(() => {
    if (error) toast.error(error?.message ?? 'Something went wrong :c');
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Yay! Marker deleted :3`);
    }
  }, [isSuccess]);

  return { mutate, error, isSuccess };
};
